import tinycolor from "tinycolor2";

/**
 * Normalized color value interface for consistent handling
 */
export interface NormalizedColorValue {
  hex: string;
  original: string;
  isValid: boolean;
}

/**
 * Validates if a CSS value represents a valid color
 * Handles modern CSS syntax that tinycolor might not recognize directly
 */
export const isValidCssColor = (value: string): boolean => {
  if (!value || typeof value !== "string") return false;

  const trimmedValue = value.trim();
  // Handle space-separated HSL/HSV syntax (e.g., "0 84.2% 60.2%") which is valid CSS but not recognized by tinycolor
  if (trimmedValue.includes("%") && !/^#|hsv|hsl|rgb|var/.test(trimmedValue)) {
    const parts = trimmedValue.split(/\s+/);

    if (parts.length >= 3) {
      const h = parts[0];
      const s = parts[1].endsWith("%") ? parts[1] : `${parts[1]}%`;
      const l = parts[2].endsWith("%") ? parts[2] : `${parts[2]}%`;
      const tinycolorHsl = `hsl(${h}, ${s}, ${l})`;
      const tc = tinycolor(tinycolorHsl);
      return tc.isValid();
    }
  }

  // For all other cases, use tinycolor directly
  const tc = tinycolor(trimmedValue);
  return tc.isValid();
};

/**
 * Normalizes any color value to hex format using TinyColor2
 */
export const normalizeColorValue = (value: string): string => {
  if (!isValidCssColor(value)) return value;

  // Handle modern CSS space-separated HSL syntax before passing to tinycolor
  const trimmedValue = value.trim();
  if (
    trimmedValue.includes("%") &&
    !trimmedValue.startsWith("#") &&
    !trimmedValue.startsWith("hsv")
  ) {
    const parts = trimmedValue.split(/\s+/);
    if (parts.length >= 3) {
      const h = parts[0];
      const s = parts[1].endsWith("%") ? parts[1] : `${parts[1]}%`;
      const l = parts[2].endsWith("%") ? parts[2] : `${parts[2]}%`;
      const tinycolorHsl = `hsl(${h}, ${s}, ${l})`;
      const color = tinycolor(tinycolorHsl);
      return color.isValid() ? color.toHexString() : value;
    }
  }

  const color = tinycolor(trimmedValue);
  return color.isValid() ? color.toHexString() : value;
};

/**
 * Extracts color value from Tailwind color class and converts to hex
 */
export const extractColorFromTailwindClass = (
  value: string,
  comboboxOptions: Array<{ tailwind: string; css: string }>
): string => {
  // Handle Tailwind classes like bg-red-500, text-blue-300, etc.
  if (
    value.startsWith("bg-") ||
    (value.includes("-") && value.split("-").length === 2)
  ) {
    const targetClass = value.startsWith("bg-") ? value : `bg-${value}`;
    const equivalentColor = comboboxOptions.find(
      (option) => option.tailwind === targetClass
    );

    if (equivalentColor) {
      return extractColorFromCssProperty(equivalentColor.css);
    }
  }

  return value;
};

/**
 * Extracts color value from CSS property string like "background-color: #ff0000;"
 */
export const extractColorFromCssProperty = (css: string): string => {
  const colorValue = css
    .replace(/^(background-color|color|border-color):\s*/, "")
    .replace(/;$/, "")
    .trim();

  return normalizeColorValue(colorValue);
};

/**
 * Handles CSS custom properties (variables starting with --)
 */
export const extractColorFromCssVariable = (value: string): string => {
  if (value.startsWith("--")) {
    const cleanedValue = value.replace(/-/g, "");
    return normalizeColorValue(cleanedValue);
  }

  return value;
};

/**
 * Comprehensive color value parser that handles all supported formats
 */
export const parseColorValue = (
  value: string,
  comboboxOptions: Array<{ tailwind: string; css: string }> = []
): NormalizedColorValue => {
  if (!value) {
    return { hex: "#000000", original: value, isValid: false };
  }

  let processedValue = value;

  // Handle CSS custom properties
  if (value.startsWith("--")) {
    processedValue = extractColorFromCssVariable(value);
  }
  // Handle Tailwind classes
  else if (
    value.startsWith("bg-") ||
    (value.includes("-") && value.split("-").length === 2)
  ) {
    processedValue = extractColorFromTailwindClass(value, comboboxOptions);
  }
  // Handle CSS properties
  else if (value.includes(":")) {
    processedValue = extractColorFromCssProperty(value);
  }
  // Handle all other color formats with TinyColor2
  else {
    processedValue = normalizeColorValue(value);
  }

  const color = tinycolor(processedValue);
  const isValid = color.isValid();

  return {
    hex: isValid ? color.toHexString() : "#000000",
    original: value,
    isValid,
  };
};

/**
 * Removes gradient-specific CSS variables from color values
 */
export const cleanGradientVariables = (value: string): string => {
  return value
    .replace("var(--tw-gradient-to-position)", "")
    .replace("var(--tw-gradient-from-position)", "")
    .replace("var(--tw-gradient-via-position)", "")
    .trim();
};

/**
 * Safely extracts color value from CSS property string
 * Replaces duplicate safeExtractValueFromCss functions
 */
export const safeExtractValueFromCss = (css: string): string | null => {
  try {
    const firstCss = css.split("\n")[0];
    let value: string = firstCss.split(":")[1];
    value = value.split(";")[0];
    return value.trim();
  } catch (error) {
    return null;
  }
};

/**
 * Cleans and normalizes color value, preferring direct value over fallback
 * Replaces cleanColorValue logic
 */
export const cleanColorValue = (
  primaryValue?: string,
  fallbackValue?: string
): string => {
  if (primaryValue && primaryValue !== fallbackValue) {
    return primaryValue;
  }
  if (fallbackValue) {
    return fallbackValue;
  }
  return "";
};

/**
 * Extracts position value from CSS or Tailwind option
 * Replaces extractPositionValue function
 */
export const extractPositionValue = (option: {
  css?: string;
  tailwind?: string;
}): string => {
  if (option.css) {
    const match = option.css.match(/:([^;]+);/);
    if (match) return match[1].trim();
  }

  if (option.tailwind) {
    const match = option.tailwind.match(/-(\d+)%/);
    if (match) return `${match[1]}%`;
  }

  return "";
};
